import { supabase } from './supabase.js';

export const readingService = {
  // Reading Session operations
  async getCurrentReadingSession(userId) {
    try {
      const { data, error } = await supabase
        .from('user_reading_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'reading')
        .order('updated_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      return data?.[0] || null;
    } catch (error) {
      console.error('❌ Error getting current reading session:', error);
      throw error;
    }
  },

  async createReadingSession(userId, bookData) {
    try {
      const { data, error } = await supabase
        .from('user_reading_sessions')
        .insert([{
          user_id: userId,
          book_title: bookData.title,
          book_author: bookData.author,
          total_pages: bookData.totalPages,
          current_page: bookData.currentPage || 0,
          status: 'reading',
          started_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error creating reading session:', error);
      throw error;
    }
  },

  async updateReadingProgress(sessionId, newPage) {
    try {
      const { data, error } = await supabase
        .from('user_reading_sessions')
        .update({
          current_page: newPage,
          updated_at: new Date().toISOString()
        })
        .eq('id', sessionId)
        .select()
        .single();

      if (error) throw error;

      // Update daily reading streak
      await this.updateDailyReadingStreak(data.user_id);

      return data;
    } catch (error) {
      console.error('❌ Error updating reading progress:', error);
      throw error;
    }
  },

  async completeBook(sessionId) {
    try {
      // First get the session details
      const { data: session, error: sessionError } = await supabase
        .from('user_reading_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;

      // Mark session as completed
      const { data, error } = await supabase
        .from('user_reading_sessions')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          current_page: session.total_pages
        })
        .eq('id', sessionId)
        .select()
        .single();

      if (error) throw error;

      // Create book completion record
      await this.recordBookCompletion(session.user_id, session);

      // Update streak and check achievements
      await Promise.all([
        this.updateDailyReadingStreak(session.user_id),
        this.checkAndAwardAchievements(session.user_id)
      ]);

      return data;
    } catch (error) {
      console.error('❌ Error completing book:', error);
      throw error;
    }
  },

  async recordBookCompletion(userId, sessionData) {
    try {
      const { data, error } = await supabase
        .from('user_reading_completions')
        .insert([{
          user_id: userId,
          book_title: sessionData.book_title,
          book_author: sessionData.book_author,
          total_pages: sessionData.total_pages,
          started_at: sessionData.started_at,
          completed_at: new Date().toISOString(),
          reading_session_id: sessionData.id
        }])
        .select()
        .single();

      if (error) throw error;

      // Award reading reward
      await this.awardBookCompletionReward(userId, sessionData);

      return data;
    } catch (error) {
      console.error('❌ Error recording book completion:', error);
      throw error;
    }
  },

  // Reading Statistics
  async getReadingStats(userId) {
    try {
      const [
        { data: completions, error: completionsError },
        { data: currentStreak, error: streakError },
        { data: rewards, error: rewardsError }
      ] = await Promise.all([
        // Get books completed this year
        supabase
          .from('user_reading_completions')
          .select('*')
          .eq('user_id', userId)
          .gte('completed_at', new Date(new Date().getFullYear(), 0, 1).toISOString()),
        
        // Get current streak
        supabase
          .from('user_reading_streaks')
          .select('*')
          .eq('user_id', userId)
          .order('updated_at', { ascending: false })
          .limit(1),
        
        // Get total rewards this month
        supabase
          .from('reading_rewards')
          .select('reward_amount')
          .eq('user_id', userId)
          .gte('earned_at', new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString())
      ]);

      if (completionsError) throw completionsError;
      if (streakError) throw streakError;
      if (rewardsError) throw rewardsError;

      const totalEarned = rewards?.reduce((sum, reward) => sum + parseFloat(reward.reward_amount), 0) || 0;

      return {
        booksRead: completions?.length || 0,
        currentStreak: currentStreak?.[0]?.current_streak || 0,
        totalEarned: totalEarned,
        pagesReadToday: await this.getPagesReadToday(userId),
        averagePagesPerDay: await this.getAveragePagesPerDay(userId)
      };
    } catch (error) {
      console.error('❌ Error getting reading stats:', error);
      throw error;
    }
  },

  async getPagesReadToday(userId) {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('reading_progress_logs')
        .select('pages_read')
        .eq('user_id', userId)
        .gte('created_at', `${today}T00:00:00.000Z`)
        .lt('created_at', `${today}T23:59:59.999Z`);

      if (error) throw error;
      return data?.reduce((sum, log) => sum + log.pages_read, 0) || 0;
    } catch (error) {
      console.error('❌ Error getting pages read today:', error);
      return 0;
    }
  },

  async getAveragePagesPerDay(userId) {
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('reading_progress_logs')
        .select('pages_read')
        .eq('user_id', userId)
        .gte('created_at', thirtyDaysAgo);

      if (error) throw error;
      const totalPages = data?.reduce((sum, log) => sum + log.pages_read, 0) || 0;
      return Math.round(totalPages / 30);
    } catch (error) {
      console.error('❌ Error getting average pages per day:', error);
      return 0;
    }
  },

  // Streak Management
  async updateDailyReadingStreak(userId) {
    try {
      const { data, error } = await supabase.rpc('update_reading_streak', {
        p_user_id: userId
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error updating reading streak:', error);
      throw error;
    }
  },

  async getCurrentStreak(userId) {
    try {
      const { data, error } = await supabase
        .from('user_reading_streaks')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      return data?.[0] || null;
    } catch (error) {
      console.error('❌ Error getting current streak:', error);
      throw error;
    }
  },

  // Rewards System
  async getReadingRewards(userId) {
    try {
      const currentMonth = new Date();
      const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).toISOString();
      
      const { data: rewards, error } = await supabase
        .from('reading_rewards')
        .select('*')
        .eq('user_id', userId)
        .gte('earned_at', startOfMonth)
        .order('earned_at', { ascending: false });

      if (error) throw error;

      // Calculate breakdown
      const breakdown = rewards?.reduce((acc, reward) => {
        switch (reward.reward_type) {
          case 'book_completion':
            acc.booksCompleted = (acc.booksCompleted || 0) + parseFloat(reward.reward_amount);
            break;
          case 'streak_bonus':
            acc.streakBonus = (acc.streakBonus || 0) + parseFloat(reward.reward_amount);
            break;
          case 'speed_bonus':
            acc.speedBonus = (acc.speedBonus || 0) + parseFloat(reward.reward_amount);
            break;
          case 'family_sharing':
            acc.familySharing = (acc.familySharing || 0) + parseFloat(reward.reward_amount);
            break;
        }
        return acc;
      }, {}) || {};

      const totalThisMonth = rewards?.reduce((sum, reward) => sum + parseFloat(reward.reward_amount), 0) || 0;

      // Get next milestone
      const nextMilestone = await this.getNextMilestone(userId);

      return {
        totalThisMonth,
        breakdown,
        nextMilestone
      };
    } catch (error) {
      console.error('❌ Error getting reading rewards:', error);
      throw error;
    }
  },

  async awardBookCompletionReward(userId, sessionData) {
    try {
      const baseReward = 0.50; // $0.50 per book
      
      const { data, error } = await supabase
        .from('reading_rewards')
        .insert([{
          user_id: userId,
          reward_amount: baseReward,
          reward_type: 'book_completion',
          reward_description: `Completed "${sessionData.book_title}"`,
          reading_session_id: sessionData.id
        }])
        .select()
        .single();

      if (error) throw error;

      // Integrate with existing allowance system
      const { supabaseService } = await import('./supabase-service');
      await supabaseService.addAllowanceTransaction(
        userId,
        baseReward,
        'addition',
        `Reading reward: Completed "${sessionData.book_title}"`
      );

      return data;
    } catch (error) {
      console.error('❌ Error awarding book completion reward:', error);
      throw error;
    }
  },

  async awardStreakBonus(userId, streakDays) {
    try {
      let bonusAmount = 0;
      let description = '';

      if (streakDays >= 30) {
        bonusAmount = 5.00;
        description = '30-day reading streak bonus!';
      } else if (streakDays >= 14) {
        bonusAmount = 2.00;
        description = '14-day reading streak bonus!';
      } else if (streakDays >= 7) {
        bonusAmount = 1.00;
        description = '7-day reading streak bonus!';
      }

      if (bonusAmount > 0) {
        const { data, error } = await supabase
          .from('reading_rewards')
          .insert([{
            user_id: userId,
            reward_amount: bonusAmount,
            reward_type: 'streak_bonus',
            reward_description: description
          }])
          .select()
          .single();

        if (error) throw error;

        // Integrate with existing allowance system
        const { supabaseService } = await import('./supabase-service');
        await supabaseService.addAllowanceTransaction(
          userId,
          bonusAmount,
          'addition',
          `Reading reward: ${description}`
        );

        return data;
      }
      return null;
    } catch (error) {
      console.error('❌ Error awarding streak bonus:', error);
      throw error;
    }
  },

  async getNextMilestone(userId) {
    try {
      const stats = await this.getReadingStats(userId);
      const currentBooks = stats.booksRead;
      
      // Define milestones
      const milestones = [5, 10, 15, 20, 25, 30, 50, 75, 100];
      const nextMilestone = milestones.find(m => m > currentBooks);
      
      if (nextMilestone) {
        const progress = currentBooks / nextMilestone;
        const reward = nextMilestone >= 50 ? '$10.00' : nextMilestone >= 25 ? '$5.00' : '$2.00';
        
        return {
          description: `Read ${nextMilestone - currentBooks} more book${nextMilestone - currentBooks > 1 ? 's' : ''} to earn ${reward} bonus!`,
          progress: progress
        };
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error getting next milestone:', error);
      return null;
    }
  },

  // Challenges
  async getActiveChallenge(userId) {
    try {
      const { data, error } = await supabase
        .from('reading_challenges')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      return data?.[0] || null;
    } catch (error) {
      console.error('❌ Error getting active challenge:', error);
      throw error;
    }
  },

  async createReadingChallenge(userId, challengeData) {
    try {
      const insertData = {
        user_id: userId,
        challenge_name: challengeData.name,
        challenge_description: challengeData.description,
        target_books: challengeData.targetBooks,
        start_date: challengeData.startDate,
        end_date: challengeData.endDate,
        reward_amount: challengeData.rewardAmount,
        reward_description: challengeData.rewardDescription,
        status: 'active'
      };

      // Add new challenge type fields if they exist
      if (challengeData.challengeType) {
        insertData.challenge_type = challengeData.challengeType;
      }
      if (challengeData.targetPoints) {
        insertData.target_points = challengeData.targetPoints;
      }
      if (challengeData.targetPages) {
        insertData.target_pages = challengeData.targetPages;
      }
      if (challengeData.pointsConfig) {
        insertData.points_config = challengeData.pointsConfig;
      }
      if (challengeData.milestoneRewards) {
        insertData.milestone_rewards = challengeData.milestoneRewards;
      }

      const { data, error } = await supabase
        .from('reading_challenges')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error creating reading challenge:', error);
      throw error;
    }
  },

  // Family Recommendations
  async getFamilyRecommendations(userId) {
    try {
      // Get reading-specific recommendations from reading_recommendations table
      const { data: readingRecs, error: readingError } = await supabase
        .from('reading_recommendations')
        .select(`
          *,
          recommender:family_members!recommended_by_user_id(id, name, avatar)
        `)
        .eq('recommended_to_user_id', userId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (readingError) throw readingError;

      // Get all book ideas from family ideas system and filter client-side
      const { data: allBookIdeas, error: ideasError } = await supabase
        .from('ideas')
        .select(`
          *,
          created_by_member:family_members!created_by(id, name, avatar)
        `)
        .eq('category', 'book')
        .order('created_at', { ascending: false });

      if (ideasError) throw ideasError;

      // Filter ideas that are for the current user or general recommendations
      const relevantBookIdeas = (allBookIdeas || []).filter(idea => {
        // Include if no specific target (general recommendation)
        if (!idea.target_kids || idea.target_kids.length === 0) return true;
        
        // Include if current user is in target list
        if (Array.isArray(idea.target_kids) && idea.target_kids.includes(userId)) return true;
        
        return false;
      });

      // Convert family book ideas to reading recommendation format
      const convertedFamilyRecs = relevantBookIdeas.map(idea => ({
        id: `idea_${idea.id}`,
        book_title: idea.title,
        book_author: idea.author || idea.link_metadata?.author,
        recommendation_reason: idea.description,
        source: 'family_library',
        status: 'pending',
        created_at: idea.created_at,
        recommender: idea.created_by_member,
        // Additional book metadata
        genre: idea.genre || idea.link_metadata?.genre,
        isbn: idea.isbn || idea.link_metadata?.isbn,
        cover_url: idea.link_metadata?.image,
        is_audiobook: idea.is_audiobook || idea.link_metadata?.isAudiobook,
        why_recommended: idea.why_recommended,
        // Original idea reference for potential actions
        original_idea_id: idea.id,
        idea_category: idea.category
      }));

      // Combine both sources and sort by creation date
      const allRecommendations = [...(readingRecs || []), ...convertedFamilyRecs]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      return allRecommendations;
    } catch (error) {
      console.error('❌ Error getting family recommendations:', error);
      throw error;
    }
  },

  async addFamilyRecommendation(userId, recommendedBy, bookData) {
    try {
      const { data, error } = await supabase
        .from('reading_recommendations')
        .insert([{
          recommended_to_user_id: userId,
          recommended_by_user_id: recommendedBy,
          book_title: bookData.title,
          book_author: bookData.author,
          recommendation_reason: bookData.reason,
          source: recommendedBy ? 'family_member' : 'family_library',
          status: 'pending'
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error adding family recommendation:', error);
      throw error;
    }
  },

  async updateRecommendationStatus(recommendationId, status) {
    try {
      const { data, error } = await supabase
        .from('reading_recommendations')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', recommendationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error updating recommendation status:', error);
      throw error;
    }
  },

  // Achievements
  async getUserAchievements(userId) {
    try {
      const { data, error } = await supabase
        .from('reading_achievements')
        .select('*')
        .eq('user_id', userId)
        .order('earned_at', { ascending: false });

      if (error) throw error;

      // Get all available achievements
      const availableAchievements = await this.getAvailableAchievements();
      
      // Merge earned and available
      const earnedIds = new Set(data?.map(a => a.achievement_type) || []);
      const available = availableAchievements.filter(a => !earnedIds.has(a.id));
      
      return {
        earned: data || [],
        available: available
      };
    } catch (error) {
      console.error('❌ Error getting user achievements:', error);
      throw error;
    }
  },

  async getAvailableAchievements() {
    // Define static achievement types - these could be moved to a database table
    return [
      {
        id: 'first_book',
        name: 'First Chapter',
        description: 'Complete your very first book',
        icon: 'book',
        rarity: 'common',
        points: 100
      },
      {
        id: 'streak_7',
        name: 'Weekly Reader',
        description: 'Read for 7 days in a row',
        icon: 'flame',
        rarity: 'common',
        points: 150
      },
      {
        id: 'speed_reader',
        name: 'Speed Reader',
        description: 'Read 50 pages in one day',
        icon: 'zap',
        rarity: 'uncommon',
        points: 250
      },
      {
        id: 'book_lover',
        name: 'Book Lover',
        description: 'Complete 5 books',
        icon: 'star',
        rarity: 'uncommon',
        points: 300
      },
      {
        id: 'family_reader',
        name: 'Family Reader',
        description: 'Read a book recommended by family',
        icon: 'users',
        rarity: 'common',
        points: 200
      },
      {
        id: 'streak_30',
        name: 'Reading Master',
        description: 'Read for 30 days in a row',
        icon: 'target',
        rarity: 'rare',
        points: 500
      }
    ];
  },

  async checkAndAwardAchievements(userId) {
    try {
      const stats = await this.getReadingStats(userId);
      const { earned } = await this.getUserAchievements(userId);
      const earnedTypes = new Set(earned.map(a => a.achievement_type));

      const toAward = [];

      // Check various achievement conditions
      if (stats.booksRead >= 1 && !earnedTypes.has('first_book')) {
        toAward.push('first_book');
      }
      
      if (stats.currentStreak >= 7 && !earnedTypes.has('streak_7')) {
        toAward.push('streak_7');
      }
      
      if (stats.currentStreak >= 30 && !earnedTypes.has('streak_30')) {
        toAward.push('streak_30');
      }
      
      if (stats.booksRead >= 5 && !earnedTypes.has('book_lover')) {
        toAward.push('book_lover');
      }

      // Check for speed reader (50 pages in one day)
      const pagesReadToday = await this.getPagesReadToday(userId);
      if (pagesReadToday >= 50 && !earnedTypes.has('speed_reader')) {
        toAward.push('speed_reader');
      }

      // Award new achievements
      for (const achievementType of toAward) {
        await this.awardAchievement(userId, achievementType);
      }

      return toAward;
    } catch (error) {
      console.error('❌ Error checking achievements:', error);
      throw error;
    }
  },

  async awardAchievement(userId, achievementType) {
    try {
      const achievements = await this.getAvailableAchievements();
      const achievement = achievements.find(a => a.id === achievementType);
      
      if (!achievement) return null;

      const { data, error } = await supabase
        .from('reading_achievements')
        .insert([{
          user_id: userId,
          achievement_type: achievementType,
          achievement_name: achievement.name,
          achievement_description: achievement.description,
          points_awarded: achievement.points,
          earned_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error awarding achievement:', error);
      throw error;
    }
  },

  // Integration with existing PaperSedge systems
  async addBookToShoppingList(userId, bookData) {
    try {
      const { supabaseService } = await import('./supabase-service');
      
      // Get user's family to add to family shopping list
      const family = await supabaseService.getFamilyByUserId(userId);
      if (!family) throw new Error('User not part of a family');

      // Add book to shopping list using existing shopping system
      const { data, error } = await supabase
        .from('shopping_items')
        .insert([{
          name: `${bookData.book_title} by ${bookData.book_author}`,
          category_id: null, // Books category would need to be set up
          requested_by: userId,
          quantity: 1,
          priority: 'normal',
          notes: 'From reading recommendations',
          status: 'needed'
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error adding book to shopping list:', error);
      throw error;
    }
  },

  async sendBookDiscussionMessage(fromUserId, toUserId, bookTitle) {
    try {
      const { supabaseService } = await import('./supabase-service');
      
      // Get user's family to send family message
      const family = await supabaseService.getFamilyByUserId(fromUserId);
      if (!family) throw new Error('User not part of a family');

      const messageData = {
        type: 'book_discussion',
        subject: `Question about book recommendation`,
        content: `Hey! I saw you recommended "${bookTitle}". Can you tell me more about why you think I'd like it?`,
        recipientIds: [toUserId],
        priority: 'normal',
        relatedEntityType: 'book_recommendation',
        relatedEntityId: null
      };

      return await supabaseService.sendFamilyMessage(family.id, messageData, fromUserId);
    } catch (error) {
      console.error('❌ Error sending book discussion message:', error);
      throw error;
    }
  },

  // Book management
  async searchBooks(query) {
    try {
      // This would integrate with a book API like Google Books or Open Library
      // For now, return mock data
      const mockBooks = [
        {
          title: 'Wonder',
          author: 'R.J. Palacio',
          totalPages: 315,
          isbn: '9780375869020',
          description: 'A story about a boy with facial differences starting school.',
          genre: 'Middle Grade Fiction'
        },
        {
          title: 'The Giver',
          author: 'Lois Lowry',
          totalPages: 179,
          isbn: '9780547995663',
          description: 'A dystopian novel about a boy living in a seemingly perfect society.',
          genre: 'Young Adult Fiction'
        }
      ].filter(book => 
        book.title.toLowerCase().includes(query.toLowerCase()) ||
        book.author.toLowerCase().includes(query.toLowerCase())
      );

      return mockBooks;
    } catch (error) {
      console.error('❌ Error searching books:', error);
      throw error;
    }
  },

  async getUserReadingList(userId) {
    try {
      const { data, error } = await supabase
        .from('user_reading_list')
        .select('*')
        .eq('user_id', userId)
        .order('added_at', { ascending: false });

      if (error) {
        // If table doesn't exist yet, return empty array
        if (error.message?.includes('does not exist')) {
          console.warn('⚠️ user_reading_list table not yet created, returning empty list');
          return [];
        }
        throw error;
      }
      return data || [];
    } catch (error) {
      console.error('❌ Error getting user reading list:', error);
      // Return empty array instead of throwing to prevent app crash
      return [];
    }
  },

  async addBookToReadingList(userId, bookData) {
    try {
      const { data, error } = await supabase
        .from('user_reading_list')
        .insert([{
          user_id: userId,
          book_title: bookData.book_title || bookData.title,
          book_author: bookData.book_author || bookData.author,
          total_pages: bookData.total_pages || bookData.totalPages,
          isbn: bookData.isbn,
          description: bookData.description,
          genre: bookData.genre,
          status: 'to_read'
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error adding book to reading list:', error);
      throw error;
    }
  }
};